import { useTranslation } from "@msuite/ui/localization";
import { Tag } from "@msuite/ui/tag";
import { guardString } from "@msuite/utils/strings";
import type { Enums, FileBatchListItem, FileCollectionTable } from "@msuite/utils/types";

/**
 * The collections a dropped file can end up in, and their locale keys.
 *
 * Written out rather than interpolated, because a `files.inbox_collections.${collection}` over the
 * fifteen registry collections is not a key that exists: the classifier files into three, the
 * offer import into a fourth, outgoing invoice files are named by the rows filed before the
 * `retire_invoice_document_kind` migration, and the other ten are reached from the record they
 * belong to. A
 * target outside this map is named by its own column, which is ugly and true — and, being a key
 * this list does not carry, it is also a collection nothing writes here yet.
 */
const COLLECTION_LABELS = {
	projectDocumentFiles: "files.inbox_collections.projectDocumentFiles",
	ticketFiles: "files.inbox_collections.ticketFiles",
	outgoingInvoiceFiles: "files.inbox_collections.outgoingInvoiceFiles",
	protocolFiles: "files.inbox_collections.protocolFiles",
	serviceCalculationFiles: "files.inbox_collections.serviceCalculationFiles",
} as const satisfies Partial<Record<FileCollectionTable, string>>;

/** Props Interface */
interface FileOutcomeProps {
	state: Enums<"fileClassificationState">;
	reason: string | null;
	error: string | null;
	/** Where the file was filed. Absent on the inbox, which by definition lists what went nowhere. */
	target?: FileBatchListItem["target"];
}

/**
 * What became of one dropped file, in one line: a tag saying which of the four things happened,
 * and then the detail that belongs to it.
 *
 * **The tag is the uniform half and the sentence beside it is the recognisable half.** The batch
 * report and the inbox render the same component, so a file reads the same in both and a person
 * scanning either is looking at colour before words: green went somewhere, amber needs them, red
 * is the pipeline's problem rather than theirs. The sentence then says which somewhere, or why
 * not — genuinely different kinds of information, which is why the state is a tag rather than
 * left implicit in the prose.
 */
export function FileOutcome({ state, reason, error, target }: FileOutcomeProps) {
	/** Context */
	const { t } = useTranslation();

	/** Functions */
	function getDestination() {
		if (!target) return guardString(reason);
		const key = COLLECTION_LABELS[target.collection as keyof typeof COLLECTION_LABELS];
		const collection = key ? t(key) : target.collection;
		if (!target.label) return `${collection} — ${t("files.outcome_unknown_record")}`;
		return `${collection} — ${target.label}`;
	}

	/** Render */
	if (state === "failed") {
		return (
			<div className="h-stack gap-2">
				<Tag
					color="red"
					variant="plain"
				>
					{t("files.inbox_failed")}:
				</Tag>
				<span className="truncate">{guardString(error)}</span>
			</div>
		);
	}

	if (state === "linked") {
		return (
			<div className="h-stack gap-2">
				<Tag
					color="emerald"
					variant="plain"
				>
					{t("files.outcome_linked")}:
				</Tag>
				<span className="truncate">{getDestination()}</span>
			</div>
		);
	}

	if (state === "unplaced") {
		return (
			<div className="h-stack gap-2">
				<Tag
					color="amber"
					variant="plain"
				>
					{t("files.outcome_unplaced")}:
				</Tag>
				<span className="truncate">{guardString(reason)}</span>
			</div>
		);
	}

	// Everything before the three settled states: the worker still has it. Reachable only from the
	// batch report, which can be opened while a drop is still being read.
	return (
		<Tag
			color="gray"
			withPing
			variant="plain"
		>
			{t("files.outcome_pending")}
		</Tag>
	);
}
